import { isAbortError } from '../utils/abortError';

export const API_BASE = process.env.REACT_APP_API_URL || '';

function getActiveBranchIdForRequest() {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (!user || user.role !== 'branch_admin' || !user.id) return null;
    const raw = localStorage.getItem(`branch_admin_active_branch_${user.id}`);
    const parsed = raw ? Number(raw) : null;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  } catch {
    return null;
  }
}

function withBranchScope(endpoint) {
  const branchId = getActiveBranchIdForRequest();
  if (!branchId || endpoint.startsWith('/api/auth/')) return endpoint;
  const sep = endpoint.includes('?') ? '&' : '?';
  if (endpoint.includes('branch_id=')) return endpoint;
  return `${endpoint}${sep}branch_id=${branchId}`;
}

export function buildQueryString(params) {
  if (!params) return '';
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      sp.set(key, String(value));
    }
  });
  const qs = sp.toString();
  return qs ? `?${qs}` : '';
}

function logRequestError(endpoint, err) {
  if (isAbortError(err)) return;
  console.error(`[API Error] ${endpoint}:`, err.message);
}

export async function request(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  const method = (options.method || 'GET').toUpperCase();
  const sendsJsonBody = ['POST', 'PUT', 'PATCH'].includes(method);

  let body = options.body;
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  // Fastify rejects application/json requests with an empty body (FST_ERR_CTP_EMPTY_JSON_BODY)
  if (sendsJsonBody) {
    if (body === undefined || body === null) body = '{}';
    if (!headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }
  }

  const scopedEndpoint = method === 'GET' ? withBranchScope(endpoint) : endpoint;
  const url = `${API_BASE}${scopedEndpoint}`;
  const fetchOptions = { ...options, headers };
  if (body !== undefined) fetchOptions.body = body;

  const branchId = getActiveBranchIdForRequest();
  if (branchId && !headers['X-Branch-Id']) {
    headers['X-Branch-Id'] = String(branchId);
  }

  try {
    const res = await fetch(url, fetchOptions);

    if (res.status === 401 && !endpoint.startsWith('/api/auth/') && !endpoint.startsWith('/api/public/')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = window.location.pathname.includes('/admin') ? '/admin/login' : '/login';
      throw new Error('انتهت صلاحية الجلسة');
    }

    if (!res.ok) {
      const text = await res.text().catch(() => '');
      let errBody = {};
      try {
        errBody = text ? JSON.parse(text) : {};
      } catch {
        errBody = {};
      }
      if (errBody.code === 'TRIAL_ENDED' && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('algeza:trial-ended', { detail: errBody }));
      }
      if (errBody.code === 'COMPANY_STOPPED' && typeof window !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
      let message = errBody.error || errBody.message;
      if (!message) {
        if (res.status === 413) {
          message = 'حجم الطلب كبير جداً — سجّل مقطعاً أقصر (أقل من 30 ثانية)';
        } else if (res.status === 502 || res.status === 504) {
          message = 'انتهت مهلة الخادم — أعد المحاولة بمقطع أقصر';
        } else if (res.status >= 500) {
          message = 'خطأ في الخادم — حاول لاحقاً';
        } else {
          message = text && text.length < 200 && !text.trim().startsWith('<')
            ? text
            : `خطأ في الاتصال بالخادم (${res.status})`;
        }
      }
      const err = new Error(message);
      err.body = errBody;
      err.status = res.status;
      throw err;
    }

    return res.json();
  } catch (err) {
    if (isAbortError(err)) throw err;
    // Network / proxy failures (no HTTP response)
    if (err && !err.status && (err.name === 'TypeError' || /Failed to fetch|NetworkError|Load failed/i.test(err.message || ''))) {
      const netErr = new Error('تعذر الاتصال بالخادم — تحقق من الشبكة وأعد المحاولة');
      logRequestError(endpoint, netErr);
      throw netErr;
    }
    logRequestError(endpoint, err);
    throw err;
  }
}

export async function requestBlob(endpoint, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const scopedEndpoint = withBranchScope(endpoint);
  const url = `${API_BASE}${scopedEndpoint}`;

  const branchId = getActiveBranchIdForRequest();
  if (branchId && !headers['X-Branch-Id']) {
    headers['X-Branch-Id'] = String(branchId);
  }

  try {
    const res = await fetch(url, { ...options, headers });

    if (res.status === 401 && !endpoint.startsWith('/api/auth/') && !endpoint.startsWith('/api/public/')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = window.location.pathname.includes('/admin') ? '/admin/login' : '/login';
      throw new Error('انتهت صلاحية الجلسة');
    }

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: 'فشل التحميل' }));
      const err = new Error(errBody.error || 'فشل التحميل');
      err.body = errBody;
      err.status = res.status;
      throw err;
    }

    return res.blob();
  } catch (err) {
    if (isAbortError(err)) throw err;
    logRequestError(endpoint, err);
    throw err;
  }
}